import { ChangeDetectionStrategy, Component, computed, effect, inject, OnInit, untracked } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { map, of } from 'rxjs';
import type { EventProcessingMode } from '../../../../core/models/browser-tab.models';
import { processingModeFromRoutePath } from '../../../../core/routing/browser-tab-routes';
import { BrowserTabNavigationService } from '../../../../core/services/browser-tab-navigation.service';
import { BrowserTabService } from '../../../../core/services/browser-tab.service';
import { EventProcessingPanel } from '../../components/event-processing-panel/event-processing-panel';

/**
 * Thin route shell for event processing — reads route params/mode and tab state,
 * then delegates UI to {@link EventProcessingPanel}.
 *
 * Paths:
 * - `/{tab}/event-id/:eventId` → process-event
 * - `/{tab}/site-check/:eventId` → site-check
 * - `/{tab}/patrol/:eventId` → manual-patrol
 */
@Component({
  selector: 'app-event-view',
  imports: [EventProcessingPanel],
  templateUrl: './event-view.html',
  styleUrl: './event-view.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EventViewPage implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly tabNavigation = inject(BrowserTabNavigationService);
  readonly browserTabs = inject(BrowserTabService);

  /** Must track paramMap reactively — snapshot stays stale when the same EventView is reused across tabs. */
  private readonly routeEventId = toSignal(
    this.route.paramMap.pipe(map((params) => params.get('eventId') ?? '')),
    { initialValue: this.route.snapshot.paramMap.get('eventId') ?? '' },
  );

  private readonly routeProcessingMode = toSignal(
    this.route.data.pipe(
      map((data) => (data['processingMode'] as EventProcessingMode | undefined) ?? null),
    ),
    {
      initialValue:
        (this.route.snapshot.data['processingMode'] as EventProcessingMode | undefined) ?? null,
    },
  );

  /** Parent `/:tabKey` — write route event onto that tab, not whichever tab is active. */
  private readonly routeTabId = toSignal(
    this.route.parent?.paramMap.pipe(map((params) => params.get('tabKey') ?? '')) ?? of(''),
    { initialValue: this.route.parent?.snapshot.paramMap.get('tabKey') ?? '' },
  );

  readonly eventId = computed(() => {
    const fromRoute = this.routeEventId();
    if (fromRoute) {
      return fromRoute;
    }

    return this.browserTabs.activeEventId() ?? '';
  });

  readonly processingMode = computed((): EventProcessingMode => {
    const fromRouteData = this.routeProcessingMode();
    if (fromRouteData) {
      return fromRouteData;
    }

    const fromRoutePath = processingModeFromRoutePath(
      this.browserTabs.activePageState().routePath,
    );
    if (fromRoutePath) {
      return fromRoutePath;
    }

    return this.browserTabs.activePageState().eventProcessingMode;
  });

  constructor() {
    effect(() => {
      const id = this.routeEventId();
      const mode = this.routeProcessingMode();
      if (!id || !mode) {
        return;
      }

      untracked(() => this.applyRouteEventToUrlTab(id, mode));
    });
  }

  ngOnInit(): void {
    const id = this.eventId();
    if (!id) {
      void this.tabNavigation.navigateWithinActiveTab();
      return;
    }

    this.applyRouteEventToUrlTab(id, this.routeProcessingMode());
  }

  private applyRouteEventToUrlTab(eventId: string, mode: EventProcessingMode | null): void {
    const tabId = this.routeTabId() || this.browserTabs.activeTabId();
    if (!tabId || !this.browserTabs.hasTab(tabId)) {
      return;
    }

    this.tabNavigation.publishRouteEvent(tabId, eventId, mode);
  }
}
